/* eslint-disable no-magic-numbers */
import { Tag, TagGroup } from "../api/model";
import AILimits from "../api/model/aiLimit/AILimits";
import ApiBarrel from "../api/model/barrels/Barrel";
import BarrelDataStage from "../api/model/barrels/BarrelDataStage";
import BarrelKind from "../api/model/barrels/BarrelKind";
import BarrelOnboardingType from "../api/model/barrels/BarrelOnboardingType";
import { Project as ApiProject, PartialProject, CompleteProject } from "../api/model/barrels/Project";
import ScreenSection from "../api/model/barrels/ScreenSection";
import { Styleguide as ApiStyleguide, PartialStyleguide, CompleteStyleguide } from "../api/model/barrels/Styleguide";
import StyleguideLink from "../api/model/barrels/StyleguideLink";
import PreviewBackgroundColor, { DEFAULT_PREVIEW_BACKGROUND_COLOR } from "../api/model/colors/PreviewBackgroundColor";
import ApiComponent from "../api/model/components/Component";
import ComponentPage from "../api/model/components/ComponentPage";
import ApiComponentSection from "../api/model/components/ComponentSection";
import ConnectedComponent from "../api/model/connectedComponents/ConnectedComponent";
import RemPreferences from "../api/model/spacings/RemPreferences";
import SpacingSection from "../api/model/spacings/SpacingSection";
import { VariableCollection } from "../api/model/variables/Variables";
import VariantGroup from "../model/VariantGroup";
import { getColorValueByName } from "../utils/dot";
import * as util from "../utils/legacy";
import { sortBy } from "../utils/sortBy";
import { Modify } from "../utils/UtilityTypes";
import { PLATFORM } from "../enums";

import BarrelIntegration from "./BarrelIntegration";
import BarrelUser from "./BarrelUser";
import Color from "./color";
import Component from "./Component";
import ComponentSection from "./ComponentSection";
import Font from "./font";
import Model from "./model";
import NoteType, { NoteTypeData } from "./NoteType";
import { PartialScreen } from "./Screen";
import TextStyleDefinition from "./TextStyleDefinition";
import WorkflowStatus, { WorkflowStatusData } from "./WorkflowStatus";

type ExtendedTextStyle = Font & { scaledFontSize?: number; };

function setScaledFontSize(textStyles: ExtendedTextStyle[], densityScale: number) {
    if (!textStyles.length) {
        return;
    }

    const maxFontSize = textStyles[0].fontSize / densityScale;
    const minFontSize = textStyles[textStyles.length - 1].fontSize / densityScale;

    if (maxFontSize <= 36 && minFontSize >= 14) {
        textStyles.forEach(textStyle => {
            textStyle.scaledFontSize = textStyle.fontSize / densityScale;
        });
    } else {
        let upperLimit;
        let lowerLimit;

        if (maxFontSize < 14) {
            upperLimit = 24;
            lowerLimit = 14;
        } else if (minFontSize > 36) {
            upperLimit = 36;
            lowerLimit = 24;
        } else {
            upperLimit = Math.min(36, maxFontSize);
            lowerLimit = Math.max(14, minFontSize);
        }

        const scaleRatio = maxFontSize === minFontSize ? 0 : (upperLimit - lowerLimit) / (maxFontSize - minFontSize);

        textStyles[0].scaledFontSize = Math.round(upperLimit);

        for (let i = 1; i < textStyles.length; i++) {
            const previousFontSize = textStyles[i - 1].fontSize / densityScale;
            const currentFontSize = textStyles[i].fontSize / densityScale;

            textStyles[i].scaledFontSize =
                Math.round(textStyles[i - 1].scaledFontSize! - (previousFontSize - currentFontSize) * scaleRatio);
        }
    }
}

function addColorSuffix(name: string, suffix: string) {
    return `${name} - ${suffix}`;
}

function getColorNameSuffix(count: number) {
    const singularNumber = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const decimalNumber = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    if (count > 19) {
        const singularCount = count % 10;
        const decimalCount = Math.floor(count / 10);

        return decimalNumber[decimalCount] + (singularCount ? ` ${singularNumber[singularCount]}` : "");
    }

    return singularNumber[count];
}

function findComponentByProp(componentSections: ComponentSection[], prop: keyof Component, value: string | number)
    : Component | null {
    let component;
    for (const componentSection of componentSections) {
        component = componentSection.components.find(comp => comp[prop] === value);
        if (component) {
            return component;
        }

        component = findComponentByProp(componentSection.componentSections, prop, value);
        if (component) {
            return component;
        }
    }

    return null;
}

function buildComponentPathsAndVersionCommits(
    componentSections: (ApiComponentSection | ComponentSection)[], path: string[] = []
): ComponentSection[] {
    return componentSections?.map(apiComponentSection => {
        const {
            components: apiComponents = [],
            componentSections: apiComponentSections = [],
            ...rest
        } = apiComponentSection;
        const sectionPath = [...path, apiComponentSection._id];
        const componentSection: Partial<ComponentSection> = { ...rest, idPath: sectionPath };

        componentSection.components = (apiComponents as (Component | ApiComponent)[])?.map(component => {
            if (component.latestVersion) {
                component.latestVersion.commit ??= {
                    message: "",
                    color: getColorValueByName("yellow"),
                    author: component.latestVersion.creator!
                };
            }

            return {
                ...component,
                idPath: [...sectionPath, component._id]
            };
        });
        componentSection.componentSections = buildComponentPathsAndVersionCommits(apiComponentSections, sectionPath);

        return componentSection as ComponentSection;
    });
}

export type BarrelData =
    Modify<
        ApiBarrel &
        Partial<PartialProject> &
        Partial<CompleteProject> &
        Partial<PartialStyleguide> &
        Partial<CompleteStyleguide> &
        Partial<ApiProject> &
        Partial<ApiStyleguide>, {
            dataStage?: BarrelDataStage;
            extensions?: string[];
            linkedProjects?: (ApiProject | Barrel)[];
            pages?: ComponentPage[];
            componentSections?: ComponentSection[] | ApiComponentSection[];
            textStyles?: TextStyleDefinition[];
            workflowStatus?: WorkflowStatusData;
            screens?: PartialScreen[];
            parent?: string | null;
            remPreferences?: RemPreferences;
            connectedComponents?: ConnectedComponent[] | null;
            localConnectedComponents?: ConnectedComponent[] | null;
            styleguideLink?: StyleguideLink | null;
            noteTypes?: NoteTypeData[];
            unpinned?: boolean;
            variableCollections?: VariableCollection[];
        }>;

export default class Barrel extends Model {
    _id: string;
    id: string;
    dataStage: BarrelDataStage;
    type: PLATFORM;
    name: string;
    description?: string;
    created: string;
    updated: string | null;
    status: string;
    kind: BarrelKind;
    users: BarrelUser[];
    integration?: BarrelIntegration;
    extensions?: string[];
    colors?: Color[];
    variableCollections?: VariableCollection[];
    textStyles: Font[];
    densityScale?: number;
    thumbnail?: string;
    previewBackgroundColor: PreviewBackgroundColor;
    icon: {
        type: string;
    };
    public?: boolean;
    spacingSections?: SpacingSection[];
    maxUserLimit?: number;
    screens?: PartialScreen[];
    sections?: ScreenSection[];
    variantGroups?: VariantGroup[];
    workflowStatus: WorkflowStatus;
    styleguideLink?: StyleguideLink | null;
    parent?: string | null;
    ancestors?: string[];
    componentSections?: ComponentSection[];
    pages?: ComponentPage[];
    connectedComponents?: ConnectedComponent[] | null;
    localConnectedComponents?: ConnectedComponent[] | null;
    remPreferences: RemPreferences;
    linkedProjects?: (ApiProject | Barrel)[];
    noteTypes?: NoteType[];
    onboardingType?: BarrelOnboardingType;
    tags?: Tag[];
    tagGroups?: TagGroup[];
    unpinned?: boolean;
    screenCount?: number;
    allowedScreenCount?: number;
    limits?: AILimits;

    constructor({
        _id,
        type,
        dataStage,
        name,
        description,
        created,
        updated,
        status,
        kind,
        users,
        integration,
        extensions,
        colors,
        variableCollections,
        densityScale,
        textStyles,
        thumbnail,
        previewBackgroundColor,
        icon,
        public: isPublic,
        sections,
        screens,
        variantGroups,
        workflowStatus,
        styleguideLink,
        componentSections,
        pages,
        parent,
        ancestors,
        connectedComponents,
        localConnectedComponents,
        linkedProjects,
        spacingSections,
        maxUserLimit,
        remPreferences,
        noteTypes,
        onboardingType,
        tags,
        tagGroups,
        unpinned,
        screenCount,
        allowedScreenCount,
        limits
    }: BarrelData) {
        super();

        this._id = _id;
        this.id = _id;
        this.type = type;
        this.name = name;
        this.description = description;
        this.created = created;
        this.updated = updated;
        this.status = status;
        this.extensions = extensions;
        this.thumbnail = thumbnail;
        this.previewBackgroundColor = previewBackgroundColor ?? DEFAULT_PREVIEW_BACKGROUND_COLOR;
        this.icon = icon;
        this.public = isPublic;
        this.spacingSections = spacingSections;
        this.maxUserLimit = maxUserLimit;
        this.densityScale = densityScale;
        this.onboardingType = onboardingType;
        this.unpinned = !!unpinned;

        this.kind = kind ?? BarrelKind.Default;
        this.dataStage = dataStage ?? BarrelDataStage.Bare;

        this.screenCount = screenCount;
        this.allowedScreenCount = allowedScreenCount;
        this.limits = limits;

        // Full barrel response doesn't have status field, and the status
        // is assumed to be "active" by default for successful responses
        this.status = status ?? "active";
        this.users = users.map(user => new BarrelUser(user)).sort(sortBy.role);
        this.extensions = extensions ?? [];
        this.integration = new BarrelIntegration(integration);

        // Styleguide partial response has colors whereas project's partial response does not have.
        this.colors = colors?.map(colorData => new Color(colorData)) ?? [];

        this.variableCollections = variableCollections;

        // Styleguide partial response has textStyles whereas project's partial response does not have.
        if (textStyles) {
            this.textStyles = textStyles.map(textStyleData => new Font(textStyleData)).sort(util.fontSort);
            setScaledFontSize(this.textStyles, this.densityScale!);
        } else {
            this.textStyles = [];
        }

        // Project specific fields
        if (screens) {
            this.screens = [...screens];
            this.screens.forEach(screen => {
                if (screen.versions) {
                    return;
                }

                screen.description ??= "";
                screen.tags ??= [];

                if (screen.latestVersion) {
                    screen.versions = [screen.latestVersion];
                    delete screen.latestVersion;
                }
            });

            const screenIds = screens.map(screen => screen._id);
            this.sections = sections?.map(section =>
                ({ ...section, screens: section.screens.filter(sid => screenIds.includes(sid)) }));

            this.variantGroups = variantGroups;
        }

        this.workflowStatus = workflowStatus
            ? new WorkflowStatus(workflowStatus)
            : WorkflowStatus.NoStatus;

        this.styleguideLink = styleguideLink;

        if (noteTypes) {
            this.noteTypes = noteTypes.map(noteType => new NoteType(noteType));
        }

        // Styleguide specific fields
        if (componentSections) {
            this.componentSections = buildComponentPathsAndVersionCommits(componentSections);
        }

        this.pages = pages;

        this.parent = parent;
        this.ancestors = ancestors;
        this.linkedProjects = linkedProjects;
        this.connectedComponents = connectedComponents;
        this.localConnectedComponents = localConnectedComponents;
        this.remPreferences = remPreferences || {
            remRootFontSize: 16,
            useForFontSizes: false,
            useForMeasurements: false
        };
        this.tags = tags;
        this.tagGroups = tagGroups;
    }

    get slackTeamId() {
        if (this.integration?.slacks?.length) {
            return this.integration.slacks[0].teamId;
        }
    }

    override toObject() {
        return {
            type: this.type,
            name: this.name,
            colors: this.colors?.map(color => color.toObject()),
            textStyles: this.textStyles?.map(ts => ts.toObject()),
            spacingSections: this.spacingSections,
            componentSections: this.componentSections,
            pages: this.pages,
            variableCollections: this.variableCollections
        };
    }

    findTextStyleById(textStyleId: string) {
        return this.textStyles.find(ts => ts.id === textStyleId);
    }

    findTextStyleByName(name: string) {
        return this.textStyles.find(ts => ts.name === name);
    }

    getTextStyleName() {
        if (!this.textStyles.length) {
            return "Text Style";
        }

        let name = "Text Style";
        let count = 1;

        while (this.findTextStyleByName(name)) {
            count++;
            name = `Text Style ${count}`;
        }

        return name;
    }

    findSpacingSectionById(ssid: string) {
        return this.spacingSections?.find(({ _id }) => _id === ssid) ?? {};
    }

    findSpacingTokenInsideSectionById(ssid: string, sptid: string) {
        const spacingSection = this.spacingSections?.find(({ _id }) => _id === ssid);

        return spacingSection?.spacingTokens.find(({ _id }) => _id === sptid) ?? {};
    }

    findUser(userId: string) {
        return this.users.find(u => u.user._id === userId);
    }

    findOwner() {
        return this.users.find(u => u.role === "owner");
    }

    findColorById(colorId: string) {
        return this.colors?.find(c => c.id === colorId);
    }

    findColorByName(name: string) {
        return this.colors?.find(c => c.name === name);
    }

    findColorEqual(color: Color) {
        return this.colors?.find(c => color.equals(c));
    }

    findColorByHexAndAlpha({ alpha, hex } : { alpha?: number; hex?: string; }) {
        return this.colors?.find(c => {
            const alphaEqual = typeof alpha === "undefined" ? true : c.a === alpha;
            const hexEqual = typeof hex === "undefined" ? true : c.hexBase() === hex.toLowerCase();

            return hexEqual && alphaEqual;
        });
    }

    checkDuplicateColorHex(hex: string, color: Color) {
        const c = this.findColorByHexAndAlpha({
            hex,
            alpha: color.a
        });

        return c && c.id !== color.id ? c : null;
    }

    checkDuplicateColorAlpha(alpha: string, color: Color) {
        const c = this.findColorByHexAndAlpha({
            hex: color.hexBase(),
            alpha: parseInt(alpha, 10) / 100
        });

        return c && c.id !== color.id ? c : null;
    }

    checkDuplicateColorName(name: string, color: Color) {
        const c = this.findColorByName(name);

        return c && c.id !== color.id ? c : null;
    }

    #checkBarrelColors = (platform: PLATFORM, color: Color) => {
        let colorName;

        for (let i = 0; i < this.colors!.length; i++) {
            const c = this.colors![i];

            if (c.hexBase() === color.hexBase()) {
                const [suffix] = c.name!.match(/\d+$/) || [];

                if (suffix && parseInt(suffix, 10) === Math.round(c.a! * 100)) {
                    [, colorName = c.name] = c.name!.match(/^([\w-]+?)[\d_-]+$/) || [];
                } else {
                    colorName = c.name;
                }

                break;
            }
        }

        colorName ??= color.generateName(platform);

        return this.#checkBarrelColorNames(addColorSuffix(colorName, Math.round(color.a! * 100).toString()));
    };

    #checkBarrelColorNames = (colorName: string) => {
        let name = colorName;
        let count = 1;

        while (this.findColorByName(name)) {
            count++;
            name = addColorSuffix(colorName, getColorNameSuffix(count));
        }

        return name;
    };

    getColorName(platform: PLATFORM, color: Color) {
        if (color.a! < 1) {
            return this.#checkBarrelColors(platform, color);
        }

        return this.#checkBarrelColorNames(color.generateName(platform));
    }

    findScreen(screenId: string) {
        return this.screens?.find(screen => screen._id === screenId);
    }

    findScreenByName(name: string) {
        return this.screens?.find(screen => screen.name === name);
    }

    // TODO-bedo: Remove if it's unused
    findVersion(screenId: string, versionId: string) {
        const screen = this.findScreen(screenId);

        if (!screen) {
            return null;
        }

        return screen.versions!.find(version => version._id === versionId);
    }

    findSection(sectionId: string) {
        if (!sectionId) {
            return null;
        }

        return this.sections!.find(section => (section._id === sectionId));
    }

    isDefaultSection(sectionId: string) {
        return this.sections!.findIndex(section => (section._id === sectionId)) === 0;
    }

    findScreenSection(screenId: string) {
        return this.sections!.find(section => section.screens.includes(screenId));
    }

    findVariantGroup(variantGroupId: string) {
        return this.variantGroups!.find(variantGroup => variantGroup._id === variantGroupId);
    }

    findComponentById(coid: string) {
        return findComponentByProp(this.componentSections!, "_id", coid);
    }

    findComponentByName(name: string) {
        return findComponentByProp(this.componentSections!, "name", name);
    }

    findComponentSection(csids: string[]): ComponentSection | Barrel | null {
        if (!csids?.length) {
            return this;
        }

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let componentSection: ComponentSection | Barrel | undefined = this;
        for (const csid of csids) {
            componentSection = componentSection.componentSections!.find(cs => cs._id === csid);
            if (!componentSection) {
                return null;
            }
        }

        return componentSection;
    }

    findComponentSectionByVariantId(vgid: string): null | ComponentSection | Barrel {
        return this.componentSections?.find(
            ({ variant }) => variant?.properties.some(({ _id }) => _id === vgid)
        ) ?? null;
    }

    findPageOfComponentSection(csid: string): ComponentPage | undefined {
        if (!this.pages?.length) {
            return undefined;
        }

        return this.pages.find(page =>
            page.componentSections.some(cs => cs.id === csid)
        );
    }
}
